import { window } from '../../../globals';
import { post } from '../../../redux/api';
import type {
  CreateOrganizationRequest,
  CreateOrganizationResponse,
  LoginNextRequest,
  LoginNextResponse,
  LoginRequest,
  LoginResponse,
  RegistrationRequest,
  RegistrationResponse,
} from '../../../../typings/api';
import type { AuthPage } from '../index';

import type { LoginStep } from './AuthContext';

/**
 * This function accepts the response of createOrganization (a user with the
 * password omitted) and updates `window.user` to reflect that.
 *
 * @param {{ data?: CreateOrganizationResponse }} response - Response from post request.
 */
function syncGlobalUserWithResponse({ data: user }: { data?: CreateOrganizationResponse }): void {
  window.user = {
    ...window.user,
    ...user,
  };
  window.initializeClientDefaults(window.user);
}

/**
 * Determines the auth "page" we are on based on the pathname.
 * @param {string} pathname - pathname, from react-router.
 */
export const getPageFromPathname = (pathname: string): AuthPage => pathname.startsWith('/auth/register')
  ? 'register'
  : 'login';

/**
 * Returns the alert text which informs the user that they must use SSO to login.
 *
 * @param {boolean} isGoogleSSO - We tailor the message a bit if the SSO method is Google.
 */
export const getSSOAlertText = (isGoogleSSO: boolean = false): string =>
  `Your organization requires you to log in using ${isGoogleSSO ? 'Google, ' : ''}your company’s single sign-on provider.`;

/**
 * Determines if the submit button should be disabled or not.
 *
 * @param {LoginStep} step - "Step" of logging in we are on.
 * @param {string} email - User's email/username.
 * @param {string} password - User's password.
 * @param {boolean} hidePasswordInput - If the password input is visible (hidden for SSO logins).
 * @param {string} mfaCode - MFA verification code.
 * @param {boolean} useBackupCode - Whether user is entering a backup code.
 * @returns {boolean}
 */
export const isSubmitBtnDisabled = ({ step, email, password, hidePasswordInput, mfaCode, useBackupCode }: {
  step: LoginStep;
  email: string;
  password: string;
  hidePasswordInput: boolean;
  mfaCode?: string;
  useBackupCode?: boolean;
}): boolean => {
  const trimmedEmail = email?.trim() ?? '';
  if (step === 'enterEmail') {
    return trimmedEmail === '';
  }
  if (step === 'verifyMfa') {
    if (useBackupCode) {
      // Backup codes are 12 characters (hyphens optional)
      const cleanCode = (mfaCode || '').replace(/[-\s]/g, '');
      return cleanCode.length !== 12;
    }
    // TOTP codes are 6 digits
    return (mfaCode?.length ?? 0) !== 6;
  }
  return trimmedEmail === '' || (!hidePasswordInput && password.length === 0);
};

/**
 * Initial request for logging in. The backend will returns info about how the user should be logging in,
 * which the UI will be using
 *
 * @param {string} username - The "username" (usually an email, unless using LDAP).
 * @param {string} [subdomain] - Subdomain the user wants to log in to.
 * @param {string} [inviteToken] - Invite token, if provided.
 */
export async function loginNext(
  username: string,
  subdomain?: string,
  inviteToken?: string,
): Promise<LoginNextResponse> {
  const { data } = await post<LoginNextRequest, LoginNextResponse>(
    '/account/login/next',
    {
      username,
      subdomain,
      inviteToken,
    },
  );
  return data as LoginNextResponse;
}

/**
 * Login request (after the loginNext request).
 *
 * @param {LoginRequest} reqBody - The object that is sent as the request body.
 */
export async function login(reqBody: LoginRequest): Promise<LoginResponse> {
  const { data } = await post<LoginRequest, LoginResponse>('/account/login', reqBody);
  return data as LoginResponse;
}

/**
 * Registration request
 *
 * @param {RegistrationRequest} reqBody - The object that is sent as the request body.
 */
export function register(reqBody: RegistrationRequest): Promise<{ data?: RegistrationResponse }> {
  return post<RegistrationRequest, RegistrationResponse>('/account/register', {
    ...reqBody,
  });
}

/**
 * Create a new organization.
 * This will be called if a non on-prem user was created without organization (i.e. not invited) or if an on-prem
 * user has chosen to create a new one
 *
 * @param {string} orgTitle - The title for the new org.
 * @param {string} adminEmail - The email from the new org admin.
 */
export async function createOrganization(orgTitle: string, adminEmail: string): Promise<void> {
  const response = await post<CreateOrganizationRequest, CreateOrganizationResponse>('/api/user/createOrganization', {
    organization_name: orgTitle,
    organization_email: adminEmail,
  });
  return syncGlobalUserWithResponse(response);
}
